import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { ClipboardIcon } from "@phosphor-icons/react/dist/csr/Clipboard";
import { useEffect, useRef, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";

const COPIED_FOR_MS = 1600;

/**
 * An address the customer has to paste into their own mail settings.
 *
 * The feedback is on the control rather than a toast, which would say the same thing
 * twice. Where the clipboard is refused the value is selected instead, so the keyboard
 * still gets them there.
 */
export const CopyField = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => {
  const [copied, setCopied] = useState(false);
  const [refused, setRefused] = useState(false);
  // Typography does not forward a ref, so the range is taken over its wrapper.
  const valueRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const selectValue = () => {
    const node = valueRef.current;
    if (!node) return;
    const range = document.createRange();
    range.selectNodeContents(node);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setRefused(false);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), COPIED_FOR_MS);
    } catch {
      setCopied(false);
      setRefused(true);
      selectValue();
    }
  };

  return (
    <Stack className="w-full" gap={1} align="stretch">
      <Text weight="semibold" variant="caption">
        {label}
      </Text>
      <Stack className="w-full" direction="row" gap={1} align="center">
        <Stack align="start" ref={valueRef} gap={0} className="min-w-0">
          <Text className="break-all select-text">{value}</Text>
        </Stack>
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon"
                type="button"
                onClick={() => void copy()}
                aria-label={`Copy ${label.toLowerCase()}, ${value}`}
              />
            }
          >
            {copied ? <CheckIcon /> : <ClipboardIcon />}
          </TooltipTrigger>
          <TooltipContent>{copied ? "Copied" : "Copy"}</TooltipContent>
        </Tooltip>
      </Stack>
      {refused && (
        <Text variant="caption" tone="muted">
          Your browser would not let us copy it. We have selected it for you.
        </Text>
      )}
    </Stack>
  );
};
